import React, { useState, useEffect, useCallback, useRef } from 'react';
import './Resources.css';
import ResourceGanttChart from './ResourceGanttChart';
import OperationModal from './OperationModal';
import ResourceModal from './ResourceModal';
import { API_BASE_URL } from "./config";
import { getTypeAgentByOKP } from "./resourcesUtils";
import { demoResources, demoOperations } from "./demoData";

/**
 * Преобразует массив [year, month, day, hour, min, sec?] в ISO-строку.
 * Месяц приходит 1-indexed (1-12), JS Date ждёт 0-indexed.
 */
const arrayToDate = (arr) => {
    if (!Array.isArray(arr) || arr.length < 3) return null;
    const [year, month, day, hour = 0, minute = 0, second = 0] = arr;
    return new Date(year, month - 1, day, hour, minute, second).toISOString();
};

/**
 * Извлекает имя операции из JSON-строки operationBody.
 */
const extractOperationName = (operationBody) => {
    if (!operationBody) return '';
    try {
        const body = JSON.parse(operationBody);
        if (body.Parameters) {
            const params = typeof body.Parameters === 'string'
                ? JSON.parse(body.Parameters)
                : body.Parameters;
            return params.name || '';
        }
    } catch (e) {
        // ignore
    }
    return '';
};

// 🛡️ Разумные рамки для дат операций
const MIN_YEAR = 2000;
const MAX_YEAR = 2100;
const EXCLUDED_STATUSES = [20, 21, 22];

export default function Resources() {
    const [resources, setResources] = useState([]);
    const [operations, setOperations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState('demo');
    const [selectedOperation, setSelectedOperation] = useState(null);
    const [selectedResource, setSelectedResource] = useState(null);

    // Защита от параллельных и повторных запросов
    const isFetchingRef = useRef(false);

    const fetchData = useCallback(async () => {
        if (isFetchingRef.current) {
            console.warn('⚠️ fetchData уже выполняется, пропускаем');
            return;
        }

        if (dataSource === 'demo') {
            setResources(demoResources);
            setOperations(demoOperations);
            return;
        }

        isFetchingRef.current = true;
        setLoading(true);

        try {
            console.log('🚀 Запрос к API...');
            console.time('fetch-both');

            const [operationsResponse, resourcesResponse] = await Promise.all([
                fetch(`${API_BASE_URL}/operations/`).then(res => {
                    if (!res.ok) throw new Error(`HTTP ${res.status} /operations/`);
                    return res.json();
                }),
                fetch(`${API_BASE_URL}/resources/`).then(res => {
                    if (!res.ok) throw new Error(`HTTP ${res.status} /resources/`);
                    return res.json();
                })
            ]);

            console.timeEnd('fetch-both');

            // ==================== РЕСУРСЫ ====================
            console.time('parse-resources');
            const processedResources = (resourcesResponse || [])
                .filter(resource => resource.status !== 500)
                .map(resource => ({
                    id: resource.agentId,
                    title: resource.nameAgent || 'Без названия',
                    type: getTypeAgentByOKP(
                        resource.typeAgent,
                        resource.agentId,
                        resource.nameAgent
                    ),
                    status: resource.status,
                    okp: resource.typeAgent
                }));
            console.timeEnd('parse-resources');

            // ==================== ОПЕРАЦИИ ====================
            console.time('parse-operations');

            let skippedByStatus = 0;
            let skippedByDate = 0;

            const processedOperations = (operationsResponse || [])
                .filter(task => {
                    const isExcluded = EXCLUDED_STATUSES.includes(Number(task.status));
                    if (isExcluded) skippedByStatus++;
                    return !isExcluded;
                })
                .map(task => {
                    const start = arrayToDate(task.timeStart);
                    const end = arrayToDate(task.timeEnd);

                    if (!start || !end) {
                        skippedByDate++;
                        return null;
                    }

                    // 🛡️ Отсекаем операции с неадекватными датами (год 1, 1648, 1970 и т.д.)
                    const startYear = new Date(start).getFullYear();
                    const endYear = new Date(end).getFullYear();

                    if (startYear < MIN_YEAR || startYear > MAX_YEAR) {
                        skippedByDate++;
                        return null;
                    }
                    if (endYear < MIN_YEAR || endYear > MAX_YEAR) {
                        skippedByDate++;
                        return null;
                    }

                    const nameFromBody = extractOperationName(task.operationBody);

                    return {
                        id: String(task.id),
                        title: nameFromBody
                            || (task.operationNumber ? `Опер. ${task.operationNumber}` : '')
                            || task.orderNumber
                            || 'Операция',
                        startDate: start,
                        endDate: end,
                        resourceId: task.agentId,
                        status: Number(task.status),
                        operationId: task.operationId,
                        operationNumber: task.operationNumber,
                        orderNumber: task.orderNumber,
                        location: task.location,
                        duration: task.length
                    };
                })
                .filter(Boolean);

            console.timeEnd('parse-operations');

            console.log(
                `📊 Ресурсов: ${processedResources.length}`
            );
            console.log(
                `📊 Операций: ${processedOperations.length} ` +
                `(отсеяно по статусу: ${skippedByStatus}, по датам: ${skippedByDate})`
            );

            // Распределение годов — для диагностики
            const years = new Set();
            processedOperations.forEach(op => {
                years.add(new Date(op.startDate).getFullYear());
            });
            console.log('📅 Годы в обработанных операциях:', [...years].sort());

            // Пересечение agentId
            const opAgents = new Set(processedOperations.map(o => o.resourceId));
            const resIds = new Set(processedResources.map(r => r.id));
            const common = [...opAgents].filter(id => resIds.has(id));
            const onlyInOps = [...opAgents].filter(id => !resIds.has(id));

            console.log(`🔗 agentId совпадают: ${common.length} из ${opAgents.size}`);
            if (onlyInOps.length > 0) {
                console.warn('⚠️ Операции без ресурса:', onlyInOps);
            }

            setResources(processedResources);
            setOperations(processedOperations);

        } catch (err) {
            console.error('💥 fetchData упал:', err.message);
            // Fallback на демо, БЕЗ смены dataSource (чтобы не зациклиться)
            setResources(demoResources);
            setOperations(demoOperations);
        } finally {
            setLoading(false);
            isFetchingRef.current = false;
        }
    }, [dataSource]);

    // Следим ТОЛЬКО за сменой dataSource
    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataSource]);

    // ==================== ОБРАБОТЧИКИ ====================
    const handleResourceClick = (resource) => {
        console.log('Выбран ресурс:', resource);
        setSelectedResource(resource);
    };

    const handleOperationClick = (operation) => {
        console.log('Выбрана операция:', operation);
        setSelectedOperation(operation);
    };

    const toggleDataSource = () => {
        const newSource = dataSource === 'demo' ? 'api' : 'demo';
        console.log('🔄 Смена источника:', dataSource, '→', newSource);
        setDataSource(newSource);
        setSelectedResource(null);
        setSelectedOperation(null);
    };

    // ==================== РЕНДЕР ====================
    return (
        <div className="resources-screen">
            <div className="resources-content">
                {/* Панель управления */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '20px',
                    padding: '16px 20px',
                    background: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: '24px', color: '#111827' }}>
                            Диаграмма Ганта ресурсов
                        </h2>
                        <p style={{ margin: '4px 0 0', color: '#6b7280' }}>
                            Визуализация операций по ресурсам
                        </p>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            background: '#f3f4f6',
                            padding: '6px',
                            borderRadius: '8px'
                        }}>
                            <span style={{
                                fontSize: '12px',
                                fontWeight: '600',
                                color: '#6b7280',
                                padding: '0 8px'
                            }}>
                                Данные:
                            </span>
                            <button
                                onClick={toggleDataSource}
                                style={{
                                    padding: '8px 16px',
                                    border: 'none',
                                    borderRadius: '6px',
                                    background: dataSource === 'demo' ? '#ff6b6b' : '#4ecdc4',
                                    color: 'white',
                                    cursor: 'pointer',
                                    fontWeight: '600',
                                    fontSize: '14px',
                                    transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                                onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                            >
                                {dataSource === 'demo' ? 'ДЕМО' : 'API'}
                            </button>
                        </div>

                        <button
                            onClick={fetchData}
                            disabled={loading}
                            style={{
                                padding: '8px 16px',
                                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                                border: 'none',
                                borderRadius: '6px',
                                color: 'white',
                                cursor: loading ? 'not-allowed' : 'pointer',
                                fontWeight: '600',
                                fontSize: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                opacity: loading ? 0.7 : 1,
                                transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => !loading && (e.target.style.transform = 'translateY(-2px)')}
                            onMouseLeave={(e) => !loading && (e.target.style.transform = 'translateY(0)')}
                        >
                            {loading ? (
                                <>
                                    <div style={{
                                        width: '16px',
                                        height: '16px',
                                        border: '2px solid rgba(255,255,255,0.3)',
                                        borderTop: '2px solid white',
                                        borderRadius: '50%',
                                        animation: 'spin 1s linear infinite'
                                    }}></div>
                                    Загрузка...
                                </>
                            ) : '🔄 Обновить'}
                        </button>
                    </div>
                </div>

                {/* Диаграмма Ганта */}
                <div style={{
                    flex: 1,
                    height: 'calc(100vh - 270px)',
                    minHeight: '600px',
                    background: 'white',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                    {loading ? (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            flexDirection: 'column',
                            gap: '16px'
                        }}>
                            <div style={{
                                width: '60px',
                                height: '60px',
                                border: '4px solid #f3f4f6',
                                borderTop: '4px solid #3b82f6',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                            }}></div>
                            <p style={{
                                color: '#6b7280',
                                fontSize: '16px',
                                fontWeight: '500'
                            }}>
                                Загрузка данных...
                            </p>
                        </div>
                    ) : (
                        <ResourceGanttChart
                            resources={resources}
                            operations={operations}
                            onResourceClick={handleResourceClick}
                            onOperationClick={handleOperationClick}
                        />
                    )}
                </div>

                {/* Статистика */}
                <div style={{
                    marginTop: '20px',
                    padding: '16px',
                    background: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '24px',
                    fontSize: '14px',
                    color: '#4b5563'
                }}>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px'
                    }}>
                        <span style={{ fontWeight: '600', color: '#111827' }}>Ресурсы:</span>
                        <span style={{ fontSize: '18px', fontWeight: '700', color: '#3b82f6' }}>
                            {resources.length}
                        </span>
                    </div>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px'
                    }}>
                        <span style={{ fontWeight: '600', color: '#111827' }}>Операции:</span>
                        <span style={{ fontSize: '18px', fontWeight: '700', color: '#10b981' }}>
                            {operations.length}
                        </span>
                    </div>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px'
                    }}>
                        <span style={{ fontWeight: '600', color: '#111827' }}>Источник:</span>
                        <span style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: dataSource === 'demo' ? '#ef4444' : '#10b981'
                        }}>
                            {dataSource === 'demo' ? 'Демо-данные' : 'API'}
                        </span>
                    </div>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px'
                    }}>
                        <span style={{ fontWeight: '600', color: '#111827' }}>Типы ресурсов:</span>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            {Array.from(new Set(resources.map(r => r.type))).map(type => (
                                <span key={type} style={{
                                    padding: '2px 8px',
                                    background: getTypeColor(type),
                                    color: 'white',
                                    borderRadius: '12px',
                                    fontSize: '12px'
                                }}>
                                    {type === 'personnel' ? '👤' : type === 'equipment' ? '🔧' : '📐'} {getTypeName(type)}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Подсказка */}
                <div style={{
                    marginTop: '12px',
                    textAlign: 'center',
                    fontSize: '12px',
                    color: '#9ca3af',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                }}>
                    <span>💡</span>
                    <span>Для просмотра деталей сделайте двойной клик по ресурсу или операции</span>
                </div>
            </div>

            {/* Модальные окна */}
            {selectedOperation && (
                <OperationModal
                    operation={selectedOperation}
                    onClose={() => setSelectedOperation(null)}
                />
            )}

            {selectedResource && (
                <ResourceModal
                    resource={selectedResource}
                    operations={operations}
                    onClose={() => setSelectedResource(null)}
                />
            )}

            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                
                .resources-screen {
                    height: calc(100vh - 70px);
                    display: flex;
                    flex-direction: column;
                    background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
                }
                
                .resources-content {
                    flex: 1;
                    padding: 20px;
                    overflow: auto;
                }
            `}</style>
        </div>
    );
}

// ==================== ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ ====================
function getTypeColor(type) {
    switch (type) {
        case 'personnel': return '#3b82f6';
        case 'equipment': return '#8b5cf6';
        case 'tool': return '#f59e0b';
        default: return '#6b7280';
    }
}

function getTypeName(type) {
    switch (type) {
        case 'personnel': return 'Персонал';
        case 'equipment': return 'Оборудование';
        case 'tool': return 'Инструмент';
        default: return 'Другой';
    }
}